// api/core/requestUtils.ts
import { apiClient } from './apiClient';
import { getChameleonHeaders } from './chameleonState';
import { notifyError, notifySuccess } from '@/utils';
import axios, { type AxiosRequestConfig } from 'axios';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/** True when handleRequest has already shown this error to the user */
export const wasNotified = (error: unknown): boolean => Boolean((error as { notified?: boolean } | null)?.notified);

export const handleRequest = async <T = unknown>(
    method: HttpMethod,
    url: string,
    data: object = {},
    successMessage: string | null = null,
    showError = true,
    showSuccess = true
): Promise<T> => {
    const { signal, ...requestData } = data as Record<string, unknown> & { signal?: AbortSignal };

    const options: AxiosRequestConfig = {
        method,
        url,
        signal,
        // Impersonation headers, read from sessionStorage at this instant — never
        // from a cached copy, so the identity can never lag behind the tab's state
        headers: getChameleonHeaders(),
    };

    if (method.toLowerCase() === 'get') {
        options.params = requestData;
    } else {
        options.data = requestData;
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = await (apiClient as any)(options);

        if (successMessage && showSuccess) {
            notifySuccess(successMessage);
        }

        return response.data as T;
    } catch (error) {
        if (!axios.isCancel(error)) {
            console.error(`Error with ${method.toUpperCase()} request to ${url}:`, error);

            if (showError) {
                const err = error as { response?: { data?: { message?: string; detail?: string }; status?: number } };
                const detail = err.response?.data?.detail;
                const errorMessage =
                    err.response?.data?.message ||
                    (typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : null) ||
                    'An error occurred';
                notifyError(`Error: ${err.response?.status} - ${errorMessage}`);
                // Callers that toast their own failure check this, so one failure is one message
                (error as { notified?: boolean }).notified = true;
            }
        }

        throw error;
    }
};

export const fetchCustomerResource = <T = unknown>(
    resource: string,
    customerId: number,
    signal?: AbortSignal
): Promise<T> => {
    return handleRequest<T>('get', `/${resource}/customer/${customerId}`, signal ? { signal } : {});
};
